import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FacebookOutlined, ShareAltOutlined } from '@ant-design/icons';
import { getImg } from "../../Api/getImg";
import { message } from 'antd';
import { useLanguage } from '../../context/LanguageContext';

export default function BlogCard({ title, image, summary, date, index, onOpenModal, blogId }) {
    const navigate = useNavigate();
    const { slug } = useParams();
    const { t } = useLanguage();

    // Function to detect if text contains Arabic characters
    const isArabic = (text) => {
        const arabicPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
        return arabicPattern.test(text);
    };

    // Check if title or summary contains Arabic
    const hasArabicContent = isArabic(title) || isArabic(summary);
    const textAlignment = hasArabicContent ? 'text-right' : 'text-left';
    const direction = hasArabicContent ? 'rtl' : 'ltr';

    const handleSeeMore = (e) => {
        e.stopPropagation();
        navigate(`/${slug}/blog/${blogId}`, { 
            state: { 
                blog: { 
                    id: blogId, 
                    title, 
                    image, 
                    description: summary, 
                    date 
                } 
            } 
        });
    };

    const handleCardClick = () => {
        navigate(`/${slug}/blog/${blogId}`, { 
            state: { 
                blog: { 
                    id: blogId, 
                    title, 
                    image, 
                    description: summary, 
                    date 
                } 
            } 
        });
    };

    const handleFacebookShare = (e) => {
        e.stopPropagation();
        
        const blogUrl = `${window.location.origin}/${slug}/blog/${blogId}`;
        
        const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(blogUrl)}`;
        
        window.open(
            shareUrl,
            'facebook-share-dialog',
            'width=626,height=436,resizable=yes,scrollbars=yes'
        );
    };

    const handleCopyLink = async (e) => {
        e.stopPropagation();
        
        try {
            const blogUrl = `${window.location.origin}/${slug}/blog/${blogId}`;
            await navigator.clipboard.writeText(blogUrl);
            message.success(t('blogCard.linkCopied'));
        } catch (err) {
            console.error('Failed to copy link:', err);
            message.error(t('blogCard.linkCopyFailed'));
        }
    };

    console.log(hasArabicContent, "hasArabicContent");

    return (
        <div
            className="w-[80vw] sm:w-[500px] bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl hover:scale-105 cursor-pointer transition-all duration-300 animate-fade-in group"
            style={{ animationDelay: `${index * 0.1}s` }}
            onClick={handleCardClick}
        >
            <div className="relative">
                <img
                    src={getImg(image)}
                    alt={title}
                    className="w-full h-48 object-cover"
                    loading="lazy"
                    onError={(e) => (e.target.src = 'https://via.placeholder.com/400x300?text=Fallback+Image')}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                
                <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                    <button
                        onClick={handleFacebookShare}
                        className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
                        title={t('blogCard.shareOnFacebook')}
                    >
                        <FacebookOutlined className="text-sm" />
                    </button>
                    
                    <button
                        onClick={handleCopyLink}
                        className="p-2 bg-gray-600 hover:bg-gray-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110"
                        title={t('blogCard.copyLink')}
                    >
                        <ShareAltOutlined className="text-sm" />
                    </button>
                </div>
            </div>
            
            <div className="p-4 bg-[#D3F4ED] sm:p-5 flex flex-col justify-between min-h-[200px]" >
                <div>
                    <h3 className={`text-base sm:text-lg font-semibold text-gray-900 mb-2 `}>
                        {title}
                    </h3>
                    <div className={`text-gray-600 text-sm sm:text-base `}>
                        <p className="line-clamp-3">
                            {summary}
                        </p>
                        <div className={`flex items-center justify-between mt-3 ${hasArabicContent ? 'flex-row-reverse' : 'flex-row'}`}>
                            <button
                                onClick={handleSeeMore}
                                className="text-blue-600 hover:text-blue-800 font-medium text-xs sm:text-sm hover:scale-105 transition-all duration-200"
                            >
                                {t('blogCard.fullArticle')}
                            </button>
                            
                            <div className="flex gap-2 sm:hidden">
                                <button
                                    onClick={handleFacebookShare}
                                    className="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-all duration-200"
                                    title={t('blogCard.shareOnFacebook')}
                                >
                                    <FacebookOutlined className="text-xs" />
                                </button>
                                <button
                                    onClick={handleCopyLink}
                                    className="p-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded-full transition-all duration-200"
                                    title={t('blogCard.copyLink')}
                                >
                                    <ShareAltOutlined className="text-xs" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                
                {date && (
                    <div className={`mt-3 pt-3 border-t border-gray-200 flex items-center justify-between ${hasArabicContent ? 'flex-row-reverse' : 'flex-row'}`}>
                        <span className="text-xs text-gray-500">
                            {new Date(date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                            })}
                        </span>
                        
                        <div className="hidden sm:flex items-center gap-2">
                            <span className="text-xs text-gray-500">{t('blogCard.share')}:</span>
                            <button
                                onClick={handleFacebookShare}
                                className="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full transition-all duration-200 hover:scale-110"
                                title={t('blogCard.shareOnFacebook')}
                            >
                                <FacebookOutlined className="text-xs" />
                            </button>
                            <button
                                onClick={handleCopyLink}
                                className="p-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded-full transition-all duration-200 hover:scale-110"
                                title={t('blogCard.copyLink')}
                            >
                                <ShareAltOutlined className="text-xs" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <style jsx>{`
                @keyframes fade-in {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .animate-fade-in {
                    animation: fade-in 0.5s ease-in-out;
                }

                .line-clamp-3 {
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                /* Custom hover effects */
                .group:hover .group-hover\:opacity-100 {
                    opacity: 1;
                }

                .group:hover .group-hover\:translate-y-0 {
                    transform: translateY(0);
                }
            `}</style>
        </div>
    );
}